import {FC, useState, useRef, useCallback} from 'react';
import {styled} from 'styled-components';

import {ApiRes} from '@shared/api/core/api_types';
import {MediaId} from '@shared/models/api_models';

import {showModal} from '@shared_web/components/core/modal';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {icon_eye_line} from '@shared_web/components/icons/icon_eye_line';

import {translate, Translate} from '@mavismart_frontend/components/translate';
import {UploadProgressBar} from '@mavismart_frontend/components/upload_progress_bar';
import {icon_delete} from '@mavismart_frontend/icons/icon_delete';
import {icon_upload_outbox_line} from '@mavismart_frontend/icons/icon_upload_outbox_line';
import {apiCall} from '@mavismart_frontend/lib/api';
import {TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';

export type Media = ApiRes<'mavismart_backend', 'POST /create-media'>;

type PreviewImage = {
  name: string;
  mediaId: MediaId;
};

interface InputFileProps {
  doorId: string;
  onMediaUploaded: (media: Media) => void;
  onMediaDeleted: (mediaId: MediaId) => void;
}

export const FileInput: FC<InputFileProps> = ({doorId, onMediaUploaded, onMediaDeleted}) => {
  const account = useAccount();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewImage, setPreviewImage] = useState<PreviewImage[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const triggerFileSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    inputRef.current?.click();
  }, []);

  const handleSaveFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      try {
        if (file) {
          const response = await apiCall('POST /create-media', {
            doorId,
            contentType: file.type,
            contentLength: file.size,
            fileName: file.name,
          });

          setPreviewImage(prev => [...prev, {name: file.name, mediaId: response.mediaId}]);

          if (!response.presignedPostUrl) {
            throw new Error('Presigned URL not found');
          }
          const {url, fields} = response.presignedPostUrl;
          const form = new FormData();
          for (const [key, value] of Object.entries(fields)) {
            form.append(key, value);
          }
          if (!('Content-Type' in fields)) {
            form.append('Content-Type', file.type);
          }
          form.append('file', file);

          // here we use XMLHttpRequest to track the upload progress
          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);

            // progress callback
            xhr.upload.onprogress = event => {
              if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                setUploadProgress(percent);
              }
            };

            xhr.onload = () => {
              if (xhr.status === 204) {
                showSuccess(translate('file_uploaded_successfully', getUserLanguage(account)));
                setUploadProgress(100);
                onMediaUploaded(response);
                resolve();
              } else {
                reject(new Error(`S3 upload failed: ${xhr.status}`));
              }
            };

            xhr.onerror = () => reject(new Error('S3 upload error'));

            xhr.send(form);
          });
        }
      } catch {
        showAlert(translate('error_uploading_file', getUserLanguage(account)));
      }
    },
    [doorId, account, onMediaUploaded]
  );

  const handleRemoveImage = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      const mediaId = e.currentTarget.getAttribute('data-id') as MediaId;
      setPreviewImage(prev => prev.filter(i => i.mediaId !== mediaId));
      try {
        if (mediaId) {
          await apiCall('POST /delete-media', {mediaId});
          showSuccess(translate('file_deleted_successfully', getUserLanguage(account)));
          onMediaDeleted(mediaId);
        }
      } catch {
        showAlert(translate('error_deleting_file', getUserLanguage(account)));
      }
    },
    [account, onMediaDeleted]
  );

  const showBigImage = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      const mediaId = e.currentTarget.getAttribute('data-id') as MediaId;
      try {
        const response = await apiCall('POST /get-media', {mediaId});
        const isVideo = response.media.contentType.startsWith('video/');
        showModal({
          title: translate('preview_image', getUserLanguage(account)),
          noCross: true,
          children: isVideo ? (
            <video src={response.media.url} style={{width: '100%'}} controls playsInline>
              <track kind="captions" />
            </video>
          ) : (
            <img src={response.media.url} alt="Preview" style={{width: '100%'}} />
          ),
        });
      } catch {
        showAlert(translate('error_fetching_file', getUserLanguage(account)));
      }
    },
    [account]
  );

  return (
    <Wrapper>
      <HiddenInput type="file" accept="image/*,video/*" ref={inputRef} onChange={handleSaveFile} />
      {uploadProgress > 0 && uploadProgress < 100 && (
        <UploadProgressBar progress={uploadProgress} />
      )}

      <UploadButton onClick={triggerFileSelect}>
        <SvgIcon icon={icon_upload_outbox_line} size={20} />
        <span className="upload-label">
          <Translate value="take_or_import_photo_video" />
        </span>
      </UploadButton>

      {previewImage.length > 0 && (
        <MediaList>
          {previewImage.map(image => (
            <MediaItem key={image.mediaId}>
              <IconButton
                type="button"
                onClick={showBigImage}
                data-id={image.mediaId}
                title="Aperçu"
              >
                <SvgIcon icon={icon_eye_line} size={18} />
              </IconButton>
              <MediaName>{image.name}</MediaName>
              <IconButton
                type="button"
                onClick={handleRemoveImage}
                data-id={image.mediaId}
                title="Supprimer"
              >
                <SvgIcon icon={icon_delete} size={18} color="transparent" />
              </IconButton>
            </MediaItem>
          ))}
        </MediaList>
      )}
    </Wrapper>
  );
};

FileInput.displayName = 'FileInput';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 18px 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;
const HiddenInput = styled.input`
  display: none;
`;

const UploadButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(90deg, #e0e7ef 60%, #f1f5f9 100%);
  border-radius: 8px;
  border: none;
  cursor: pointer;
  color: ${TEXT_MAIN_COLOR};
  font-size: 1rem;
  font-weight: 500;
  padding: 10px 0 10px 16px;
  transition: background 0.2s;

  &:hover {
    background: #e2e8f0;
  }

  .upload-label {
    flex: 1;
    text-align: left;
  }
`;

const MediaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const MediaItem = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  padding: 8px 12px;
  gap: 10px;
  min-height: 40px;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
`;

const MediaName = styled.div`
  flex: 1;
  font-size: 0.98rem;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0 2px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s;
  &:hover {
    background: #e2e8f0;
  }
`;
